import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const { t, language } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const [notFound, setNotFound] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNotFound(false);

    try {
      const res = await fetch(`${API}/api/emails/password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, language }),
      });

      if (res.status === 404) {
        setNotFound(true);
        return;
      }
      if (!res.ok) throw new Error('Error');
      setSent(true);
      toast.success(language === 'es' ? 'Correo enviado' : 'Email sent');
    } catch {
      toast.error(language === 'es' ? 'Error al enviar el correo' : 'Error sending email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <Navbar />
      <main className="pt-40 pb-20 px-6">
        <div className="max-w-md mx-auto">
          <div className="card-apple p-8">
            {sent ? (
              <div className="text-center" data-testid="reset-email-sent">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h1 className="text-2xl font-semibold text-[#1D1D1F] mb-3">
                  {language === 'es' ? 'Correo enviado' : 'Email sent'}
                </h1>
                <p className="text-[#6E6E73] mb-6">
                  {language === 'es'
                    ? 'Hemos enviado un enlace a tu correo electrónico para restablecer tu contraseña. Revisa tu bandeja de entrada y la carpeta de spam.'
                    : 'We have sent a link to your email to reset your password. Check your inbox and spam folder.'}
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-[#E63946] hover:underline font-medium"
                  data-testid="back-to-login-link"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {language === 'es' ? 'Volver al inicio de sesión' : 'Back to login'}
                </Link>
              </div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <h1 className="text-2xl font-semibold text-[#1D1D1F] mb-2" data-testid="forgot-password-title">
                    {language === 'es' ? 'Restablecer contrasena' : 'Reset password'}
                  </h1>
                  <p className="text-[#6E6E73] text-sm">
                    {language === 'es'
                      ? 'Introduce tu correo electrónico y te enviaremos un enlace para crear una nueva contraseña.'
                      : 'Enter your email and we will send you a link to create a new password.'}
                  </p>
                </div>

                {notFound && (
                  <div className="mb-5 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="no-account-alert">
                    <p className="text-sm text-amber-800 font-medium mb-1">
                      {language === 'es'
                        ? 'No existe una cuenta con este correo electrónico.'
                        : 'No account found with this email address.'}
                    </p>
                    <p className="text-sm text-amber-700">
                      {language === 'es'
                        ? 'Verifica tu correo o '
                        : 'Check your email or '}
                      <Link to="/register" className="text-[#E63946] hover:underline font-medium">
                        {language === 'es' ? 'crea una cuenta nueva' : 'create a new account'}
                      </Link>.
                    </p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#1D1D1F] font-medium">
                      {language === 'es' ? 'Correo electrónico' : 'Email'}
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#86868B]" />
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setNotFound(false); }}
                        placeholder="correo@ejemplo.com"
                        className={`pl-12 py-6 rounded-xl bg-white ${notFound ? 'border-amber-400' : 'border-[#D2D2D7]'}`}
                        required
                        data-testid="forgot-password-email-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full py-6 rounded-xl bg-[#E63946] hover:bg-[#D62828] text-white font-medium"
                    disabled={loading}
                    data-testid="forgot-password-submit-btn"
                  >
                    {loading
                      ? (language === 'es' ? 'Enviando...' : 'Sending...')
                      : (language === 'es' ? 'Enviar enlace de restablecimiento' : 'Send reset link')}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 text-sm text-[#6E6E73] hover:text-[#1D1D1F]"
                    data-testid="back-to-login-from-forgot"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {language === 'es' ? 'Volver al inicio de sesión' : 'Back to login'}
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
